<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">
        {{ $t('monthlySaleInventoryCogs.pageTitle') }}
      </h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('monthlySaleInventoryCogs.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <div class="row">
      <div class="col-md-3">
        <div class="form-group">
          <ts-card>
            <ts-card-wrapper>
              <ts-loading-banner :loading="loading">
                <form>
                  <fieldset>
                    <legend>
                      {{ $t('monthlySaleInventoryCogs.criteria') }}
                    </legend>

                    <!-- Service Date Range -->
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label class="required">{{
                            $t('monthlySaleInventoryCogs.serviceDateRange')
                          }}</label>
                          <a-range-picker
                            :allowClear="false"
                            format="MM-YYYY"
                            picker="month"
                            :defaultValue="rangeDate"
                            @change="dateRangeChange"
                            class="ts-w-full"
                          />
                          <div class="validate" v-if="errors.has('date_range')">
                            {{ errors.first('date_range') }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Branch -->
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>{{
                            $t('stockTransferReport.branchName')
                          }}</label>
                          <a-select
                            v-model="model.branch_id"
                            :options="branches"
                            class="ts-w-full"
                            showSearch
                            mode="multiple"
                            :filterOption="
                              (input, option) =>
                                option.componentOptions.children[0].text
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                            "
                            @change="changeBranch"
                            :maxTagCount="1"
                          ></a-select>
                          <div class="validate" v-if="errors.has('branch_id')">
                            {{ errors.first('branch_id') }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Warehouse -->
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>{{
                            $t('stockTransferReport.warehouse')
                          }}</label>
                          <a-select
                            v-model="model.location_id"
                            :options="locations"
                            mode="multiple"
                            class="ts-w-full"
                            showSearch
                            :filterOption="
                              (input, option) =>
                                option.componentOptions.children[0].text
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                            "
                            :maxTagCount="1"
                            :placeholder="$t('all')"
                          ></a-select>
                          <div class="validate" v-if="errors.has('location_id')">
                            {{ errors.first('location_id') }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Category -->
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>{{
                            $t('inventoryListReport.category')
                          }}</label>
                          <a-select
                            v-model="model.category_id"
                            :options="categories"
                            mode="multiple"
                            showSearch
                            @change="clearGroup" 
                            :filterOption="
                              (input, option) =>
                                option.componentOptions.children[0].text
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                            "
                            class="ts-w-full"
                            :placeholder="$t('all')"
                          ></a-select>
                          <div class="validate" v-if="errors.has('category_id')">
                            {{ errors.first('category_id') }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Product Group -->
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>{{
                            $t('stockTransferReport.productGroup')
                          }}</label>
                          <a-select
                            v-model="model.pro_group_id"
                            mode="multiple"
                            :options="groupsList"
                            show-search
                            @change="clearProduct"
                            class="ts-w-full"
                            :placeholder="$t('all')"
                            :filterOption="
                              (input, option) =>
                                option.componentOptions.children[0].text
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                            "
                          ></a-select>
                          <div class="validate" v-if="errors.has('pro_group_id')">
                            {{ errors.first('pro_group_id') }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Product -->
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>{{ $t('stockTransferReport.product') }}</label>
                          <a-select
                            v-model="model.product_id"
                            mode="multiple"
                            :loading="on_search_product"
                            @search="onProductSearch"
                            :disabled="showSearchProduct"
                            :show-arrow="false"
                            :filter-option="true"
                            :not-found-content="null"
                            :placeholder="$t('all')"
                            :filterOption="
                              (input, option) =>
                                option.componentOptions.children[0].text
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                            "
                          >
                            <a-select-option
                              v-for="product in productList"
                              :key="product.value"
                              :value="product.value"
                            >
                              {{ product.label }}
                            </a-select-option>
                          </a-select>
                          <div class="validate" v-if="errors.has('product_id')">
                            {{ errors.first('product_id') }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Report Type -->
                    <div class="row form-group">
                      <a-radio-group
                        v-model="model.report_type"
                        class="col-md-12"
                      >
                        <a-radio-button value="1" class="col-md-6"
                          >Summary</a-radio-button
                        >
                        <a-radio-button value="2" class="col-md-6"
                          >Details</a-radio-button
                        >
                      </a-radio-group>
                    </div>

                   <!-- Currency -->
                    <div class="row mb-3">
                      <div class="col-md-12">
                        <label class="form-col-label control-label required">{{
                          $t('trialBalance.currency')
                        }}</label><br />
                        <RadioGroup v-model="model.currency_code" size="small">
                          <Radio
                            class="mt-1"
                            :label="c.currency_code"
                            border
                            v-for="(c, index) in currencies"
                            :key="index"
                          >
                            {{ c.currency_code }}
                          </Radio>
                        </RadioGroup>
                        <div class="text-danger" v-if="errors.has('currency_code')">
                          {{ errors.first('currency_code') }}
                        </div>
                      </div>
                    </div>

                    <!-- Actions -->
                    <div class="row ts-mt-3">
                      <div class="col-md-6 ts-text-right">
                        <ts-button
                          outline
                          color="success"
                          class="btn-block"
                          @click.prevent="exportExcel"
                          :waiting="exporting"
                        >
                          <i class="far fa-file-excel"></i>
                          {{ $t('export') }}
                        </ts-button>
                      </div>
                      <div class="col-md-6 ts-text-right">
                        <ts-button
                          color="primary"
                          outline
                          class="btn-block"
                          @click.prevent="preview"
                          :waiting="waiting"
                        >
                          <i class="far fa-file-pdf"></i>
                          {{ $t('preview') }}
                        </ts-button>
                      </div>
                    </div>

                  </fieldset>
                </form>
              </ts-loading-banner>
            </ts-card-wrapper>
          </ts-card>
        </div>
      </div>

      <!-- Preview Panel -->
      <div class="col-md-9">
        <ts-card>
          <ts-card-wrapper>
            <form>
              <fieldset>
                <legend>{{ $t('stockTransferReport.preview') }}</legend>
                <ts-preview-pdf v-model="waiting" :src="src" />
              </fieldset>
            </form>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import moment from 'moment'
import { mapActions, mapState  } from 'vuex'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'

export default {
  name: 'monthlySaleInventoryCogs',
  data() {
    return {
      waiting: false,
      loading: false,
      exporting: false,
      on_search_product: false,
      errors: new Errors(),
      productList: [],
      branches: [],
      locations: [],
      groups: [],
      categories: [],
      src: '',
      rangeDate: [moment().startOf('month'), moment().startOf('month')],
      model: {
        branch_id: [],
        location_id: [],
        category_id: [],
        pro_group_id: [],
        product_id: [],
        report_type: '1',
        currency_code: 'USD',
        year_from: moment().format('YYYY'),
        month_from: moment().format('M'),
        year_to: moment().format('YYYY'),
        month_to: moment().format('M')
      }
    }
  },
  async created() {
    this.loading = true
    await this.fetchGroups()
    await this.fetchCategories()
    await this.getBranchs()
    await this.getCurrency()
    this.loading = false
  },
  computed: {
    ...mapState('report/resource', { allCurrencies: 'currencies' }),
    currencies() {
      return this.allCurrencies.filter(c => c.currency_code !== 'THB')
    },
    showSearchProduct() {
      return !this.model.location_id || this.model.location_id.length === 0
    },
    groupsList() {
      if (this.model.category_id && this.model.category_id.length > 0) {
        return this.groups
          .filter(g => _.includes(this.model.category_id, g.category_id))
          .map(el => ({ value: el.pro_group_id, label: el.pro_group_name_en }))
      }
      return this.groups.map(el => ({
        value: el.pro_group_id,
        label: el.pro_group_name_en
      }))
    }
  },
  methods: {
    ...mapActions('report/resource', [
      'getBranch',
      'productGroup',
      'productCategory',
      'productProfileLocation',
      'getCurrency'
    ]),
    moment,

    async fetchGroups() {
      await this.productGroup()
        .then(response => {
          this.groups = response.data
        })
        .catch(error => this.$toasted.error(error.message))
    },

    async fetchCategories() {
      await this.productCategory()
        .then(response => {
          this.categories = response.data.map(el => ({
            value: el.category_id,
            label: el.category_name_en
          }))
        })
        .catch(error => this.$toasted.error(error.message))
    },

    async getBranchs() {
      await this.getBranch()
        .then(async response => {
          this.branches = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
          await this.getLocations()
        })
        .catch(error => this.$toasted.error(error.message))
    },

    changeBranch() {
      this.model.location_id = []
      this.clearProduct()
      this.getLocations()
    },

    async getLocations() {
      this.locations = []
      await this.$store
        .dispatch('report/inventory/getLocation', {
          branch_id: this.model.branch_id
        })
        .then(response => {
          this.locations = response.data.map(el => ({
            value: el.location_id,
            label: el.location_name_en
          }))
        })
        .catch(error => this.$toasted.error(error.message))
    },

    clearProduct() {
      this.model.product_id = []
      this.model.pro_group_id = []
      this.productList = []
    },

    clearGroup() {
      this.model.pro_group_id = []
      this.model.product_id = []
      this.productList = []
    },

    onProductSearch: _.debounce(function (value) {
      this.on_search_product = true
      this.productProfileLocation({
        filter: {
          search: value,
          pro_group_id: this.model.pro_group_id
        }
      })
        .then(response => {
          const incoming = response.data.map(el => ({
            value: el.product_id,
            label: el.barcode + '|' + el.product_name_en
          }))
          this.productList = _.uniqBy(
            [...this.productList, ...incoming],
            'value'
          ).slice(0, 300)
        })
        .catch(error => this.$toasted.error(error.message))
        .finally(() => {
          this.on_search_product = false
        })
    }, 500),

    preview() {
      this.errors = new Errors()
      this.waiting = true
      this.src = ''
      this.$store
        .dispatch('report/inventory/monthlySaleInventoryCogs', this.model)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },

    exportExcel() {
      this.exporting = true
      this.errors = new Errors()
      this.$store
        .dispatch('report/inventory/monthlySaleInventoryCogsExcel', this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.exporting = false
        })
    },

    dateRangeChange(rangeDate) {
      this.model.year_from  = rangeDate[0].format('YYYY')
      this.model.month_from = rangeDate[0].format('M')
      this.model.year_to    = rangeDate[1].format('YYYY')
      this.model.month_to   = rangeDate[1].format('M')
    }
  },

  beforeRouteLeave(to, from, next) {
    next()
  }
}
</script>